import { 
  buildWCUrl, 
  getDefaultHeaders,
  type APIResponse
} from '../../config/api.enhanced';

/**
 * Tap Payment Service
 * Handles all Tap payment-related API calls
 */

export interface TapPaymentRequest {
  booking_data: {
    trip_id: number;
    date: string;
    timeslot_id: number;
    boat_type: string;
    adults: number;
    children: number;
    infants: number;
    number_of_people: number;
    addons?: Array<{
      addon_id: number;
      quantity: number;
    }>;
  };
  customer_info: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  payment_method?: string;
}

export interface TapPaymentResponse {
  success: boolean;
  payment_url?: string;
  order_id?: number;
  charge_id?: string;
  ui_mode?: 'popup' | 'redirect';
  public_key?: string;
  message?: string;
}

export interface TapVerificationResponse {
  success: boolean;
  order_status: string;
  order_status_name?: string; // ✅ Custom translated status label from backend
  payment_status: string;
  charge_id?: string;
  message?: string;
}

class TapPaymentService {
  private async request<T = any>(
    url: string,
    options: RequestInit = {},
    useAuth: boolean = true
  ): Promise<APIResponse<T>> {
    try {
      const headers = {
        ...getDefaultHeaders(),
        ...options.headers,
      };

      if (useAuth) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || `HTTP ${response.status}: ${response.statusText}`,
          data: data
        };
      }

      return {
        success: true,
        data,
        message: data.message,
        timestamp: new Date().toISOString()
      };

    } catch (error) {

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Create a new Tap payment and get checkout URL
   * Now uses the unified boat-order endpoint with payment_method='tap'
   */
  async createPayment(paymentData: TapPaymentRequest): Promise<APIResponse<TapPaymentResponse>> {
    // Use unified endpoint instead of the old /boat-payment/tap/create
    const url = buildWCUrl('/boat-order');

// Transform data to match unified endpoint expectations
    const unifiedPayload = {
      product_id: paymentData.booking_data.trip_id,
      date: paymentData.booking_data.date,
      time: '', // Will be extracted from timeslot_id if needed
      adults: paymentData.booking_data.adults,
      children: paymentData.booking_data.children,
      infants: paymentData.booking_data.infants,
      passengers: [], // Will be populated based on counts
      contact_name: `${paymentData.customer_info.first_name} ${paymentData.customer_info.last_name}`,
      contact_email: paymentData.customer_info.email,
      contact_phone: paymentData.customer_info.phone,
      payment_method: 'tap', // Critical: tells backend to handle as Tap payment
      addons: paymentData.booking_data.addons || [],
      special_requests: '',
      hold_id: '',
      coupon_code: ''
    };
    
    // Use authentication for this endpoint
    const response = await this.request<TapPaymentResponse>(url, {
      method: 'POST',
      body: JSON.stringify(unifiedPayload)
    }, true); // Pass true to use authentication

    if (!response.success) {
      // Return error message from backend (already translated)
      throw new Error(response.message || 'Payment initialization failed');
    }

    return response;
  }

  /**
   * Verify payment status for an order
   */
  async verifyPayment(orderId: number): Promise<APIResponse<TapVerificationResponse>> {
    const url = buildWCUrl(`/boat-payment/tap/verify/${orderId}`);

const response = await this.request<TapVerificationResponse>(url);

    if (!response.success) {
      // Return error message from backend (already translated)
      throw new Error(response.message || 'Payment verification failed');
    }

    return response;
  }

  /**
   * Open Tap payment in popup mode
   * Uses a simple popup window instead of complex SDK integration
   */
  openPopup(paymentUrl: string, orderId: number): Window | null {
    const width = 500;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const windowFeatures = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,status=no,location=no`;

const paymentWindow = window.open(paymentUrl, 'TapPayment', windowFeatures);
    
    if (!paymentWindow) {

      // Fallback: redirect current window
      window.location.href = paymentUrl;
      return null;
    }

    // Monitor popup close
    const checkWindow = setInterval(() => {
      if (paymentWindow.closed) {
        clearInterval(checkWindow);

      }
    }, 1000);

    return paymentWindow;
  }

  /**
   * Open Tap payment page in a new window
   * Returns a promise that resolves when the window is opened
   */
  openPaymentWindow(paymentUrl: string): Window | null {
    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const windowFeatures = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`;
    
    const paymentWindow = window.open(paymentUrl, 'TapPayment', windowFeatures);
    
    if (!paymentWindow) {

      // Fallback: redirect current window
      window.location.href = paymentUrl;
    }

    return paymentWindow;
  }

  /**
   * Poll payment status until completed or failed
   * Useful for checking payment status after user returns from Tap page
   */
  async pollPaymentStatus(
    orderId: number, 
    maxAttempts: number = 20, 
    intervalMs: number = 3000
  ): Promise<TapVerificationResponse> {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const result = await this.verifyPayment(orderId);

          if (result.success && result.data) {
            // Check if payment is in a final state
            const finalStatuses = ['CAPTURED', 'AUTHORIZED', 'FAILED', 'DECLINED', 'CANCELLED'];
            if (finalStatuses.includes(result.data.payment_status)) {
              clearInterval(pollInterval);
              resolve(result.data);
              return;
            }
          }

          // Stop polling after max attempts
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            reject(new Error('Payment verification timeout'));
          }
        } catch (error) {
          clearInterval(pollInterval);
          reject(error);
        }
      }, intervalMs);
    });
  }

  /**
   * Handle callback from Tap payment page
   * Parse URL parameters and verify payment
   */
  async handleCallback(params: URLSearchParams): Promise<{
    orderId: number;
    status: string;
    message: string;
  }> {
    const orderId = params.get('order_id');

    if (!orderId) {
      throw new Error('Order ID missing in callback URL');
    }

    // Verify payment status with backend
    const verification = await this.verifyPayment(parseInt(orderId, 10));

    if (!verification.success || !verification.data) {
      throw new Error(verification.message || 'Payment verification failed');
    }

    return {
      orderId: parseInt(orderId, 10),
      status: verification.data.payment_status,
      message: this.getStatusMessage(verification.data.payment_status)
    };
  }

  /**
   * Get user-friendly message for payment status
   * Note: These are fallback messages. UI should use i18n translations instead.
   */
  private getStatusMessage(status: string): string {
    const messages: Record<string, string> = {
      'CAPTURED': 'Payment successful',
      'AUTHORIZED': 'Payment authorized',
      'FAILED': 'Payment failed',
      'DECLINED': 'Payment declined',
      'CANCELLED': 'Payment cancelled',
      'PENDING': 'Payment pending',
      'INITIATED': 'Payment initiated'
    };

    return messages[status] || 'Unknown payment status';
  }

  /**
   * ✅ NEW: Retry payment for pending Tap orders
   * Allows users to reinitialize payment for orders that failed during Tap charge creation
   */
  async retryPayment(orderId: number): Promise<APIResponse<TapPaymentResponse>> {
    const url = buildWCUrl('/boat-payment/tap/retry');
    
    const response = await this.request<TapPaymentResponse>(url, {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId })
    }, true); // Requires authentication

    if (!response.success) {
      // Return error message from backend (already translated)
      throw new Error(response.message || 'Payment retry failed');
    }

    return response;
  }
}

export default new TapPaymentService();
